/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { findComponent } from 'lib/utils/object-utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { smoothScrollToElement } from 'lib/hooks/useScrollElementIntoView';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { JumpNavContextProvider, useJumpNavContext, useMainHeaderHeight } from 'helpers/Context/JumpNavContext';
import { focusUnfocusableElement } from 'lib/utils/dom-utils/tab';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const JumpNavContainer = (props)=>{
    const isEditing = useIsEditing();
    const { DynamicPlaceholderId } = props?.params || {};
    const [isOpen, setIsOpen] = useState(false);
    const { getDictionaryValue } = useDictionary();
    const phKey = `jump-nav-container-${DynamicPlaceholderId}`;
    const headerHeight = useMainHeaderHeight();
    /*
   * Rendering
   */ const { base, mainGrid, sideContent, jumpNav, mainContent, navHeading, navigationForMobile, navigationMobileButon, navigationMobileList, navigationMobileListWrapper, jumpNavAccoordionList, contentWrapper } = TAILWIND_VARIANTS();
    const jumpNavItems = findComponent(props.rendering, 'JumpNavSection');
    const jumpNavStyle = {
        top: `${headerHeight}px`
    };
    return /*#__PURE__*/ _jsx("section", {
        className: base(),
        "data-component": "authorable/shared/layout/jump-nav-container",
        ...editingHydrationProps(isEditing),
        id: props.rendering.uid,
        ...getTestProps(`component-jump-nav-container-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsx(JumpNavContextProvider, {
            entries: jumpNavItems,
            children: /*#__PURE__*/ _jsxs("div", {
                className: mainGrid(),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: sideContent(),
                        children: /*#__PURE__*/ _jsxs("aside", {
                            className: jumpNav(),
                            style: jumpNavStyle,
                            "aria-label": "Page section navigation",
                            children: [
                                /*#__PURE__*/ _jsx(EditingHelpText, {
                                    children: "Edit below to give a different text for the sidebar. If left blank, it will show the section header in the real site."
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: navHeading(),
                                    ...getTestProps(`heading`),
                                    children: getDictionaryValue('TableOfContent') || 'Table of contents'
                                }),
                                JumpNavList(jumpNavItems, 'desktop')
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: mainContent(),
                        ...getTestProps(`main-content`),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: navigationForMobile(),
                                id: "jumpnav-mobile",
                                style: jumpNavStyle,
                                ...getTestProps(`jumpnav-mobile`),
                                children: [
                                    /*#__PURE__*/ _jsxs("button", {
                                        onClick: ()=>setIsOpen(!isOpen),
                                        className: navigationMobileButon(),
                                        ...getTestProps(`mobile-btn`),
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: getDictionaryValue('TableOfContent') || 'Table of contents'
                                            }),
                                            /*#__PURE__*/ _jsx(SvgIcon, {
                                                icon: isOpen ? 'accordion-minus' : 'accordion-plus',
                                                size: "s",
                                                viewBox: "0 0 20 21",
                                                fill: "currentColor"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: navigationMobileListWrapper({
                                            isOpen
                                        }),
                                        ...getTestProps(`jumpnav-mobile-list-wrapper`),
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: `${navigationMobileList()}`,
                                            children: /*#__PURE__*/ _jsx("aside", {
                                                "aria-label": "Page section navigation",
                                                className: jumpNavAccoordionList(),
                                                children: JumpNavList(jumpNavItems, 'mobile')
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: contentWrapper(),
                                children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                    name: phKey,
                                    rendering: props.rendering,
                                    ...getTestProps(`jump-nav-ph`)
                                })
                            })
                        ]
                    })
                ]
            })
        })
    });
};
export const Default = withStandardComponentWrapper(JumpNavContainer, false);
const JumpNavList = (jumpNavItems, context)=>{
    const { jumpNavListWrapper } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("ul", {
        className: jumpNavListWrapper(),
        role: "menubar",
        ...getTestProps(`jumpnav-list`),
        children: jumpNavItems.map((item, index)=>/*#__PURE__*/ _jsx("li", {
                role: "menuitem",
                children: /*#__PURE__*/ _jsx(JumpNavItem, {
                    item: item,
                    context: context,
                    ...getTestProps(`jumpnav-item-${index}`)
                })
            }, item.uid))
    });
};
function JumpNavItem({ item, context }) {
    const { navTitleLinkWrapper } = TAILWIND_VARIANTS();
    const { activeEntry, forceSetActiveEntry } = useJumpNavContext();
    const isActive = activeEntry?.uid === item.uid;
    return /*#__PURE__*/ _jsx(LinkWrapper, {
        ctaComponentClass: "default",
        className: navTitleLinkWrapper({
            isActive
        }),
        ctaVariant: "custom",
        field: {
            href: '#',
            anchor: 'accordion-' + item.uid
        },
        id: `jumpnav-${context}-${item.uid}`,
        "aria-label": `jumpnav-${item.fields?.heading?.value}`,
        "aria-current": isActive,
        onClick: (e)=>{
            e.preventDefault();
            const elem = document.getElementById('accordion-' + item.uid);
            const mbElement = document.getElementById('jumpnav-mobile');
            const additionalHeight = mbElement?.getBoundingClientRect()?.height || 0;
            if (elem && item.uid) {
                // Force set this item as active immediately
                forceSetActiveEntry(item.uid);
                smoothScrollToElement(elem, 'header', null, 0, additionalHeight);
                // Focus the element after scrolling
                focusUnfocusableElement(elem, true);
            }
        },
        children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
            field: item.fields?.jumpNavHeading,
            fallbacks: [
                item.fields?.heading
            ]
        })
    });
}
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [],
        mainGrid: [
            'grid',
            'grid-cols-3',
            'gap-layout-base-gutter'
        ],
        sideContent: [
            'hidden',
            'lg:block',
            'col-span-1',
            'order-2'
        ],
        jumpNav: [
            'sticky',
            'flex',
            'flex-col',
            'pl-margin-tight',
            'gap-space-between-tight',
            'border-l',
            'border-l-color-border-border'
        ],
        navTitleLinkWrapper: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'font-normal',
            'relative',
            'group',
            'text-color-text-text',
            'hover:text-color-text-link-hover'
        ],
        mainContent: [
            'col-span-3',
            'lg:col-span-2',
            'order-1'
        ],
        jumpNavListWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8'
        ],
        navHeading: [
            'text-color-text-dark',
            'font-typography-font-family-body',
            'text-typography-font-size-body-xsmall',
            'font-semibold'
        ],
        navigationMobileListWrapper: [
            'grid',
            'overflow-hidden',
            'transition-[grid-template-rows]',
            'duration-500',
            'ease-in-out'
        ],
        navigationForMobile: [
            'block',
            'sticky',
            'lg:hidden',
            'px-spacing-spacing-12',
            'py-spacing-spacing-16',
            'bg-color-surface-surface-secondary',
            'flex',
            'flex-col',
            'rounded-component-jump-nav-mobile-radius'
        ],
        jumpNavAccoordionList: [
            'mt-spacing-spacing-16'
        ],
        navigationMobileList: [
            'min-h-0'
        ],
        navigationMobileButon: [
            'w-full',
            'flex',
            'justify-between'
        ],
        contentWrapper: [
            'mt-layout-base-gutter',
            'lg:mt-0'
        ]
    },
    variants: {
        isActive: {
            true: {
                navTitleLinkWrapper: [
                    'font-semibold'
                ]
            },
            false: {}
        },
        isOpen: {
            true: {
                navigationMobileListWrapper: [
                    'grid-rows-[1fr]'
                ]
            },
            false: {
                navigationMobileListWrapper: [
                    'grid-rows-[0fr]'
                ]
            }
        }
    }
});
